import type { ReactNode } from "react";
import { useTheme } from "@/context/ThemeContext";
import { Badge } from "@/components/ui/badge";
import { ListingStatus } from "@/types/listing";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

interface CurrentBidProps {
  currentBid: number;
  taxType: string;
  status?: ListingStatus;
  startingPrice?: number;
  myHighestBid?: number | null;
  isLeading?: boolean;
  reservedPrice?: number;
  /** Hide Moms/VMB label (e.g. guest mode). */
  hideTaxType?: boolean;
  /** Rendered directly under the price (e.g. Avgifter + Leverans buttons). */
  slotAfterPrice?: ReactNode;
}

const getStatusBadge = (status: ListingStatus | undefined, biLabels: Record<string, string>) => {
  switch (status) {
    case "reserved":
      return { label: biLabels.statusReserved, className: "bg-orange-700 text-white" };
    case "active":
      return { label: biLabels.statusActive, className: "bg-green-600 text-white" };
    case "completed":
      return { label: biLabels.statusCompleted, className: "bg-gray-500 text-white" };
    case "ended":
      return { label: biLabels.statusEnded, className: "bg-red-500 text-white" };
    case "cancelled":
      return { label: biLabels.statusCancelled, className: "bg-red-600 text-white" };
    default:
      return null;
  }
};

const CurrentBid = ({
  currentBid,
  taxType,
  status,
  startingPrice,
  myHighestBid,
  isLeading,
  reservedPrice,
  hideTaxType = false,
  slotAfterPrice,
}: CurrentBidProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { language } = useLanguage();
  const labels = carDetailsLabels(language);
  const biddingLabels = labels.bidding;
  const biLabels = labels.biddingInfo;

  const statusBadge = getStatusBadge(status, biLabels);

  return (
    <div className="space-y-0.5">
      <div className="bidding-current-price-row flex items-center justify-between mb-2">
        {status === "reserved" && reservedPrice ? (
          <>
            <span className="text-base md:text-xs lg:text-sm text-primary/70 font-medium md:mr-2">
              {biLabels.reservedFor}
            </span>
            <span className="font-bold text-foreground tabular-nums text-3xl md:text-xl lg:text-3xl">
              {reservedPrice.toLocaleString()} kr
            </span>
          </>
        ) : currentBid != undefined && currentBid != 0 ? (
          <>
            <span className="text-base md:text-xs lg:text-sm text-primary/70 font-medium md:mr-2">
              {biLabels.currentBid}
            </span>
            <span className="font-bold text-foreground tabular-nums text-3xl md:text-xl lg:text-3xl">
              {currentBid.toLocaleString()} kr
            </span>
          </>
        ) : (
          <>
            <span className="text-base md:text-xs lg:text-sm text-primary/70 font-medium md:mr-2">
              {biLabels.startingPrice}
            </span>
            <span className="font-bold text-foreground tabular-nums text-3xl md:text-xl lg:text-3xl">
              {startingPrice?.toLocaleString()} kr
            </span>
          </>
        )}
      </div>
      {myHighestBid != null && myHighestBid > 0 && !isLeading && (
        <div className="flex items-center justify-between mt-2">
          <span className={`text-base md:text-xs lg:text-sm ${isDark ? "text-orange-300/90" : "text-orange-700/90"} font-medium md:mr-2`}>
            {biLabels.yourHighestBid}
          </span>
          <span className={`text-2xl md:text-lg lg:text-2xl font-bold ${isDark ? "text-orange-400" : "text-orange-600"}`}>
            {myHighestBid.toLocaleString()} kr
          </span>
        </div>
      )}
      <div className="mt-2 md:mt-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {!hideTaxType && (
            <p
              className="text-sm md:text-xs text-primary/60 p-1 md:p-0.5 rounded-lg inline-block"
            >
              {taxType === "moms" || taxType === "vat"
                ? biddingLabels.vatCar || "Moms-bil"
                : biddingLabels.vmb || "VMB"}
              {(taxType === "moms" || taxType === "vat") &&
                (biddingLabels.vatCarIncl || "")}
            </p>
          )}
          {statusBadge && (
            <Badge className={`text-sm md:text-[10px] px-2 py-0.5 md:px-1.5 md:py-0.5 ${statusBadge.className} w-fit`}>
              {statusBadge.label}
            </Badge>
          )}
        </div>
        {slotAfterPrice && <div className="flex items-center gap-1.5 flex-shrink-0">{slotAfterPrice}</div>}
      </div>
    </div>
  );
};

export default CurrentBid;
